import { HTTPError } from 'ky'
import type {
  GtmDemoRunErrorCode,
  GtmDemoRunResponse,
} from '@laplace/contracts'
import { apiClient } from '@/shared/api/client'

/** Every failure the demo can show, each with its own copy in `gtm` i18n. */
export type GtmDemoErrorKey =
  | GtmDemoRunErrorCode
  | 'rate_limited'
  | 'unavailable'
  | 'expired'

const RUN_ERROR_CODES: ReadonlySet<string> = new Set<GtmDemoRunErrorCode>([
  'invalid_url',
  'site_unreachable',
  'site_blocked',
  'site_empty',
  'no_leads',
  'leads_unavailable',
  'generation_failed',
  'capacity_reached',
  'internal',
])

export function isGtmDemoRunErrorCode(
  value: unknown,
): value is GtmDemoRunErrorCode {
  return typeof value === 'string' && RUN_ERROR_CODES.has(value)
}

export class GtmDemoRequestError extends Error {
  constructor(readonly key: GtmDemoErrorKey) {
    super(key)
    this.name = 'GtmDemoRequestError'
  }
}

async function readErrorCode(error: HTTPError): Promise<unknown> {
  try {
    const body = (await error.response.clone().json()) as {
      errorCode?: unknown
    }
    return body.errorCode
  } catch {
    return null
  }
}

/**
 * The API answers with `{ errorCode }` for demo-specific failures; plain
 * statuses cover the rest (rate-limit guard, disabled feature flag, bad id).
 */
export async function toGtmDemoRequestError(
  error: unknown,
  context: 'create' | 'poll',
): Promise<GtmDemoRequestError> {
  if (!(error instanceof HTTPError)) {
    return new GtmDemoRequestError('internal')
  }

  const code = await readErrorCode(error)
  if (isGtmDemoRunErrorCode(code)) {
    return new GtmDemoRequestError(code)
  }

  switch (error.response.status) {
    case 429:
      return new GtmDemoRequestError('rate_limited')
    case 404:
      return new GtmDemoRequestError(
        context === 'create' ? 'unavailable' : 'expired',
      )
    case 400:
      return new GtmDemoRequestError(
        context === 'create' ? 'invalid_url' : 'expired',
      )
    default:
      return new GtmDemoRequestError('internal')
  }
}

export async function createGtmDemoRun(
  url: string,
): Promise<GtmDemoRunResponse> {
  try {
    return await apiClient.gtm.createDemoRun({ url })
  } catch (error) {
    throw await toGtmDemoRequestError(error, 'create')
  }
}

export async function fetchGtmDemoRun(id: string): Promise<GtmDemoRunResponse> {
  try {
    return await apiClient.gtm.getDemoRun(id)
  } catch (error) {
    throw await toGtmDemoRequestError(error, 'poll')
  }
}
